/**
 * Theme Toggle Component
 * 
 * Button to toggle between light/dark/auto themes.
 * Shows appropriate icon and provides keyboard accessibility.
 */

import React from 'react';
import { Button, Tooltip } from 'antd';
import { BulbFilled, MoonFilled } from '@ant-design/icons';
import { useTheme } from '../../hooks/useTheme';

export function ThemeToggle() {
  const { preference, toggleTheme } = useTheme();
  const { mode, effectiveTheme } = preference;

  /**
   * Get appropriate icon based on current mode
   */
  const getIcon = () => {
    if (mode === 'auto') {
      // Auto mode: show system preference icon with subtle indication
      return effectiveTheme === 'dark' ? <MoonFilled /> : <BulbFilled />;
    }
    return mode === 'dark' ? <MoonFilled /> : <BulbFilled />;
  };

  /**
   * Get tooltip text
   */
  const getTooltip = () => {
    if (mode === 'auto') {
      return `Auto (${effectiveTheme}) - Click to switch to light mode`;
    }
    return mode === 'light' 
      ? 'Light mode - Click to switch to dark mode' 
      : 'Dark mode - Click to switch to auto mode';
  };

  /**
   * Handle keyboard events for accessibility
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleTheme();
    }
  };

  return (
    <Tooltip title={getTooltip()} placement="bottom">
      <Button
        type="text"
        icon={getIcon()}
        onClick={toggleTheme}
        onKeyDown={handleKeyDown}
        aria-label={`Current theme: ${mode}. Click to toggle theme.`}
        aria-pressed={mode !== 'auto'}
        style={{
          fontSize: '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />
    </Tooltip>
  );
}
